import { useCallback, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FileUploadProps {
  /** Accepted MIME types or extensions (e.g. "image/*,.pdf") */
  accept?: string;
  /** Max file size in MB */
  maxSizeMB?: number;
  multiple?: boolean;
  disabled?: boolean;
  /** Called with validated files */
  onChange?: (files: File[]) => void;
  className?: string;
}

interface FileEntry {
  file: File;
  previewUrl: string | null;
  loading: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isImage(file: File) {
  return file.type.startsWith("image/");
}

function isPdf(file: File) {
  return file.type === "application/pdf";
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PdfIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      className="text-red-500 dark:text-red-400"
      aria-hidden="true"
    >
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="14,2 14,8 20,8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontSize="5"
        fontWeight="700"
        fill="currentColor"
        fontFamily="sans-serif"
      >
        PDF
      </text>
    </svg>
  );
}

function GenericFileIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      className="text-content-tertiary"
      aria-hidden="true"
    >
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="14,2 14,8 20,8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      className="text-content-tertiary"
      aria-hidden="true"
    >
      <path
        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="17,8 12,3 7,8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="3"
        x2="12"
        y2="15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

// ── FilePreview ───────────────────────────────────────────────────────────────

function FilePreview({
  entry,
  onRemove,
}: {
  entry: FileEntry;
  onRemove: () => void;
}) {
  return (
    <div className="relative group flex flex-col items-center gap-1.5 p-2 rounded-lg border border-border-default bg-surface-raised w-24 shrink-0">
      {/* Thumbnail / icon */}
      <div className="w-16 h-16 rounded overflow-hidden flex items-center justify-center bg-surface-overlay">
        {entry.loading ? (
          <div className="w-full h-full skeleton-shimmer rounded" />
        ) : entry.previewUrl ? (
          <img
            src={entry.previewUrl}
            alt={entry.file.name}
            className="w-full h-full object-cover"
          />
        ) : isPdf(entry.file) ? (
          <PdfIcon />
        ) : (
          <GenericFileIcon />
        )}
      </div>

      {/* Filename */}
      <p className="w-full text-[10px] text-content-secondary truncate text-center leading-tight">
        {entry.file.name}
      </p>

      {/* File size */}
      <p className="text-[10px] text-content-tertiary">
        {(entry.file.size / 1024 / 1024).toFixed(2)} MB
      </p>

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
        aria-label={`Remove ${entry.file.name}`}
      >
        <RemoveIcon />
      </button>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FileUpload({
  accept,
  maxSizeMB = 10,
  multiple = false,
  disabled = false,
  onChange,
  className,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const validateAndAdd = useCallback(
    (incoming: FileList | File[]) => {
      const newErrors: string[] = [];
      const valid: File[] = [];

      Array.from(incoming).forEach((file) => {
        if (file.size > maxSizeMB * 1024 * 1024) {
          newErrors.push(`"${file.name}" exceeds ${maxSizeMB} MB limit.`);
          return;
        }
        if (accept) {
          const accepted = accept.split(",").map((s) => s.trim());
          const match = accepted.some((a) => {
            if (a.startsWith(".")) return file.name.toLowerCase().endsWith(a.toLowerCase());
            if (a.endsWith("/*")) return file.type.startsWith(a.replace("/*", "/"));
            return file.type === a;
          });
          if (!match) {
            newErrors.push(`"${file.name}" is not an accepted file type.`);
            return;
          }
        }
        valid.push(file);
      });

      setErrors(newErrors);

      if (valid.length === 0) return;

      const newEntries: FileEntry[] = valid.map((file) => ({
        file,
        previewUrl: null,
        loading: isImage(file),
      }));

      setEntries((prev) => {
        const next = multiple ? [...prev, ...newEntries] : newEntries;
        onChange?.(next.map((e) => e.file));
        return next;
      });

      // Build image previews asynchronously
      newEntries.forEach((entry) => {
        if (!isImage(entry.file)) return;
        const url = URL.createObjectURL(entry.file);
        const img = new Image();
        img.onload = () => {
          setEntries((prev) =>
            prev.map((e) =>
              e.file === entry.file ? { ...e, previewUrl: url, loading: false } : e,
            ),
          );
        };
        img.src = url;
      });
    },
    [accept, maxSizeMB, multiple, onChange],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    validateAndAdd(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      validateAndAdd(e.target.files);
      e.target.value = "";
    }
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index);
      next[index - 1]?.previewUrl && URL.revokeObjectURL(prev[index].previewUrl!);
      onChange?.(next.map((e) => e.file));
      return next;
    });
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload files"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            inputRef.current?.click();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors duration-150",
          "cursor-pointer select-none",
          dragging
            ? "border-brand-purple bg-violet-50 dark:bg-violet-500/12"
            : "border-border-default hover:border-brand-purple hover:bg-surface-overlay",
          disabled && "pointer-events-none opacity-50",
        )}
      >
        <UploadIcon />
        <div>
          <p className="text-sm font-medium text-content-primary">
            Drop files here or{" "}
            <span className="text-brand-purple dark:text-violet-400 underline underline-offset-2">browse</span>
          </p>
          <p className="mt-0.5 text-xs text-content-tertiary">
            {accept
              ? `Accepted: ${accept}`
              : "Any file type"}{" "}
            · Max {maxSizeMB} MB{multiple ? " · Multiple files allowed" : ""}
          </p>
        </div>
      </div>

      {/* Hidden input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleInputChange}
        className="hidden"
        aria-hidden="true"
      />

      {/* Validation errors */}
      {errors.length > 0 && (
        <ul className="flex flex-col gap-1">
          {errors.map((err, i) => (
            <li
              key={i}
              className="text-xs text-red-500 dark:text-red-400 flex items-start gap-1"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 mt-px" aria-hidden="true">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {err}
            </li>
          ))}
        </ul>
      )}

      {/* Previews */}
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {entries.map((entry, i) => (
            <FilePreview key={`${entry.file.name}-${i}`} entry={entry} onRemove={() => removeEntry(i)} />
          ))}
        </div>
      )}
    </div>
  );
}

FileUpload.displayName = "FileUpload";
